import * as _ from "lodash";
import { ILease } from "../PA.Utils/PA.UtilsIF";
import { GenericLease } from "../PA.Utils/GenericLease";
import { LeaseRegistry } from "../PA.Utils/LeaseRegistry";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { WB_WorkflowStatusRow } from "../PA.Core/PAdbSchemaRows";
import * as AsyncLock from "async-lock";
import { esWB_Workflow_Status } from "../PA.Core/PAdbSchema";
import { Workflow_Status_Type_Initial } from "../PA.MD.Core/MD.Constants";

export class WorkflowStatusService {
    private static _dbContext: PAdbContext;

    static dctWorkflowStatusByWBID: { [id: number]: WB_WorkflowStatusRow } = {};
    static dctWorkflowInitialStatusByWBID: { [id: number]: WB_WorkflowStatusRow } = {};
    static dctWorkflowByStatusID: { [id: string]: WB_WorkflowStatusRow[] } = {};
    static dctWorkflowByStatusType: { [type: string]: WB_WorkflowStatusRow[] } = {};

    static dctLock = new AsyncLock();
    static bIsLoaded: boolean = false;
    private static _lease: ILease;

    static getWorkflowStatusByWBID(wbID: number): Promise<WB_WorkflowStatusRow> {
        return new Promise<WB_WorkflowStatusRow>((resolve, error) => {
            WorkflowStatusService.dctLock.acquire("global", (done) => {
                WorkflowStatusService.checkLease()
                    .then(() => {
                        if (wbID in WorkflowStatusService.dctWorkflowStatusByWBID) {
                            done(null, WorkflowStatusService.dctWorkflowStatusByWBID[wbID]);
                            resolve(WorkflowStatusService.dctWorkflowStatusByWBID[wbID]);
                            return;
                        } else if (
                            WorkflowStatusService.bIsLoaded &&
                            !(wbID in WorkflowStatusService.dctWorkflowStatusByWBID)
                        ) {
                            done(null, WorkflowStatusService.dctWorkflowStatusByWBID[wbID]);
                            resolve(undefined);
                            return;
                        }

                        WorkflowStatusService.loadFromDB()
                            .then(() => {
                                done(null, WorkflowStatusService.dctWorkflowStatusByWBID[wbID]);
                                return WorkflowStatusService.dctWorkflowStatusByWBID[wbID];
                            })
                            .then((targetType) => {
                                resolve(targetType);
                            })
                            .catch((err) => {
                                error(err);
                            });
                    })
                    .catch((err) => {
                        error(err);
                    });
            });
        });
    }

    static getInitialWorkflowStatusByWorkflowWbID(workflowWbID: number): Promise<WB_WorkflowStatusRow> {
        return new Promise<WB_WorkflowStatusRow>((resolve, error) => {
            WorkflowStatusService.dctLock.acquire("global", (done) => {
                WorkflowStatusService.checkLease()
                    .then(() => {
                        if (workflowWbID in WorkflowStatusService.dctWorkflowInitialStatusByWBID) {
                            done(null, WorkflowStatusService.dctWorkflowInitialStatusByWBID[workflowWbID]);
                            resolve(WorkflowStatusService.dctWorkflowInitialStatusByWBID[workflowWbID]);
                            return;
                        } else if (
                            WorkflowStatusService.bIsLoaded &&
                            !(workflowWbID in WorkflowStatusService.dctWorkflowInitialStatusByWBID)
                        ) {
                            done(null, WorkflowStatusService.dctWorkflowInitialStatusByWBID[workflowWbID]);
                            resolve(undefined);
                            return;
                        }

                        WorkflowStatusService.loadFromDB()
                            .then(() => {
                                done(null, WorkflowStatusService.dctWorkflowInitialStatusByWBID[workflowWbID]);
                                return WorkflowStatusService.dctWorkflowInitialStatusByWBID[workflowWbID];
                            })
                            .then((targetType) => {
                                resolve(targetType);
                            })
                            .catch((err2) => {
                                error(err2);
                            });
                    })
                    .catch((err) => {
                        error(err);
                    });
            });
        });
    }

    /** Returns all status rows that have the given Status_ID. Will return an empty array if there are no matches */
    static getWorkflowStatusByStatusID(statusId: string): Promise<WB_WorkflowStatusRow[]> {
        if (statusId.length === 0) {
            Promise.resolve([]);
        }
        return new Promise<WB_WorkflowStatusRow[]>((resolve, error) => {
            WorkflowStatusService.dctLock.acquire("global", (done) => {
                WorkflowStatusService.checkLease()
                    .then(() => {
                        if (statusId in WorkflowStatusService.dctWorkflowByStatusID) {
                            done(null, WorkflowStatusService.dctWorkflowByStatusID);
                            resolve(WorkflowStatusService.dctWorkflowByStatusID[statusId]);
                            return;
                        }

                        if (
                            WorkflowStatusService.bIsLoaded &&
                            !(statusId in WorkflowStatusService.dctWorkflowByStatusID)
                        ) {
                            done(null, WorkflowStatusService.dctWorkflowByStatusID);
                            resolve([]);
                            return;
                        }

                        WorkflowStatusService.loadFromDB()
                            .then(() => {
                                done(null, WorkflowStatusService.dctWorkflowByStatusID);
                                resolve(WorkflowStatusService.dctWorkflowByStatusID[statusId] ?? []);
                                return;
                            })
                            .catch((err) => {
                                error(err);
                            });
                    })
                    .catch((err) => {
                        error(err);
                    });
            });
        });
    }

    /** Returns all status rows that have the given Status_IDs. Will return an empty array if there are no matches */
    static async getManyWorkflowStatusByStatusIds(statusIds: string[]): Promise<WB_WorkflowStatusRow[]> {
        const retVal: WB_WorkflowStatusRow[] = [];
        for (const status of statusIds) {
            const rows = await WorkflowStatusService.getWorkflowStatusByStatusID(status);
            retVal.push(...rows);
        }
        return retVal;
    }

    /** Returns all status rows that have the given Status_Type. Will return an empty array if there are no matches */
    static async getWorkflowStatusByStatusType(statusType: string): Promise<WB_WorkflowStatusRow[]> {
        if (statusType.length === 0) {
            Promise.resolve([]);
        }
        return new Promise<WB_WorkflowStatusRow[]>((resolve, error) => {
            WorkflowStatusService.dctLock.acquire("global", (done) => {
                WorkflowStatusService.checkLease()
                    .then(() => {
                        if (statusType in WorkflowStatusService.dctWorkflowByStatusType) {
                            done(null, WorkflowStatusService.dctWorkflowByStatusType);
                            resolve(WorkflowStatusService.dctWorkflowByStatusType[statusType]);
                            return;
                        }

                        if (
                            WorkflowStatusService.bIsLoaded &&
                            !(statusType in WorkflowStatusService.dctWorkflowByStatusType)
                        ) {
                            done(null, WorkflowStatusService.dctWorkflowByStatusType);
                            resolve([]);
                            return;
                        }

                        WorkflowStatusService.loadFromDB()
                            .then(() => {
                                done(null, WorkflowStatusService.dctWorkflowByStatusType);
                                resolve(WorkflowStatusService.dctWorkflowByStatusType[statusType] ?? []);
                                return;
                            })
                            .catch((err) => {
                                error(err);
                            });
                    })
                    .catch((err) => {
                        error(err);
                    });
            });
        });
    }

    /** Returns all status rows that have the given Status_Types. Will return an empty array if there are no matches */
    static async getManyWorkflowStatusByStatusTypes(statusTypes: string[]): Promise<WB_WorkflowStatusRow[]> {
        const retVal: WB_WorkflowStatusRow[] = [];
        for (const status of statusTypes) {
            const rows = await WorkflowStatusService.getWorkflowStatusByStatusType(status);
            retVal.push(...rows);
        }
        return retVal;
    }

    private static async loadFromDB(): Promise<void> {
        WorkflowStatusService.bIsLoaded = false;

        if (_.isUndefined(WorkflowStatusService._dbContext)) {
            WorkflowStatusService.instantiateDbContext();
        }

        const results: WB_WorkflowStatusRow[] = await WorkflowStatusService._dbContext.findAll(esWB_Workflow_Status, {});
        if (_.isArray(results)) {
            const rows: WB_WorkflowStatusRow[] = results as WB_WorkflowStatusRow[];

            _.forEach(rows, (row) => {
                WorkflowStatusService.dctWorkflowStatusByWBID[row.cif.WB_STATUS_ID] = row;

                if (row.cif.STATUS_TYPE == Workflow_Status_Type_Initial) {
                    WorkflowStatusService.dctWorkflowInitialStatusByWBID[row.cif.WB_WORKFLOW_ID] = row;
                }

                if (!(row.cif.STATUS_ID in WorkflowStatusService.dctWorkflowByStatusID)) {
                    WorkflowStatusService.dctWorkflowByStatusID[row.cif.STATUS_ID] = [];
                }
                WorkflowStatusService.dctWorkflowByStatusID[row.cif.STATUS_ID].push(row);

                if (!(row.cif.STATUS_TYPE in WorkflowStatusService.dctWorkflowByStatusType)) {
                    WorkflowStatusService.dctWorkflowByStatusType[row.cif.STATUS_TYPE] = [];
                }
                WorkflowStatusService.dctWorkflowByStatusType[row.cif.STATUS_TYPE].push(row);
            });
        }
        WorkflowStatusService.bIsLoaded = true;
    }

    private static async checkLease() {
        if (!WorkflowStatusService._lease) {
            WorkflowStatusService.createLease();
        }

        if (await WorkflowStatusService._lease.hasDataChanged()) {
            WorkflowStatusService.reset();
            await WorkflowStatusService.loadFromDB();
        }
    }

    private static createLease() {
        WorkflowStatusService._lease = new GenericLease(LeaseRegistry.Lease_Subject_StatusType);
    }

    static reset() {
        WorkflowStatusService.dctWorkflowInitialStatusByWBID = {};
        WorkflowStatusService.dctWorkflowStatusByWBID = {};
        WorkflowStatusService.dctWorkflowByStatusID = {};
        WorkflowStatusService.dctWorkflowByStatusType = {};
        WorkflowStatusService.bIsLoaded = false;
        WorkflowStatusService.instantiateDbContext();
    }

    private static instantiateDbContext() {
        WorkflowStatusService._dbContext = new PAdbContext();
    }
}
